<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import Button from 'primevue/button';
import Drawer from 'primevue/drawer';
import DiffViewer from '@/components/DiffViewer.vue';
import { formatLocalDateTime } from '@/lib/dateFormat';
import {
  readRecoverableDrafts,
  removeRecoverableDraft,
  type RecoverableDraft,
} from '@/lib/draftRecovery';

// Wide, left-positioned, non-modal drawer (same Drawer component as HelpDrawer). Brings
// back the dialog's master/detail: the left column lists every file with unsaved work
// from a timed-out session; selecting one shows a single reused DiffViewer (logout-time
// baseline vs the recovered draft) — one Monaco diff instance, not N. "Recover & open"
// stages the draft into the files store (parent's @recover) and navigates the main view
// to that file's editor. Discard drops it. Non-modal so the editor stays reachable;
// closing keeps any un-actioned drafts (AppShell's "Recover (N)" reopens this).
const props = defineProps<{ visible: boolean }>();
const emit = defineEmits<{
  'update:visible': [boolean];
  recover: [RecoverableDraft];
}>();

const items = ref<RecoverableDraft[]>([]);
const selectedPath = ref<string | null>(null);

const internalVisible = computed<boolean>({
  get: () => props.visible,
  set: (v) => emit('update:visible', v),
});

watch(
  () => props.visible,
  (v) => {
    if (v) {
      items.value = readRecoverableDrafts();
      selectedPath.value = items.value[0]?.path ?? null;
    }
  },
  { immediate: true },
);

const selected = computed(
  () => items.value.find((d) => d.path === selectedPath.value) ?? null,
);

function dropFromList(path: string) {
  removeRecoverableDraft(path);
  items.value = items.value.filter((d) => d.path !== path);
  if (selectedPath.value === path) {
    selectedPath.value = items.value[0]?.path ?? null;
  }
  if (items.value.length === 0) {
    internalVisible.value = false;
  }
}

function recover(item: RecoverableDraft) {
  emit('recover', item);
  dropFromList(item.path);
}

function discard(item: RecoverableDraft) {
  dropFromList(item.path);
}
</script>

<template>
  <Drawer
    :visible="internalVisible"
    position="left"
    :modal="false"
    :show-close-icon="false"
    :dismissable="false"
    :block-scroll="false"
    class="recovery-drawer"
    :style="{ width: '64rem', maxWidth: '95vw' }"
    @update:visible="(v: boolean) => (internalVisible = v)"
  >
    <template #header>
      <div class="header">
        <span class="title">
          <i class="pi pi-undo" aria-hidden="true" />
          Recover unsaved changes
        </span>
        <Button
          icon="pi pi-times"
          severity="secondary"
          size="small"
          text
          rounded
          title="Close — keeps the remaining drafts for this session"
          aria-label="Close recovery drawer"
          @click="internalVisible = false"
        />
      </div>
    </template>

    <p class="intro">
      You were signed out for inactivity with unsaved edits open. Pick a file to review
      the diff, then <strong>Recover &amp; open</strong> to restore your changes and jump
      to it in the editor, or <strong>Discard</strong> to drop them. Closing this panel
      keeps the remaining drafts for this browser session.
    </p>

    <div v-if="selected" class="recovery-body">
      <ul class="file-list">
        <li
          v-for="item in items"
          :key="item.path"
          :class="['file-row', { active: item.path === selectedPath }]"
          @click="selectedPath = item.path"
        >
          <div class="file-meta">
            <span class="file-path mono">{{ item.path }}</span>
            <small>captured {{ formatLocalDateTime(new Date(item.capturedAt).toISOString()) }}</small>
          </div>
          <div class="file-actions">
            <Button
              label="Recover & open"
              icon="pi pi-undo"
              size="small"
              @click.stop="recover(item)"
            />
            <Button
              label="Discard"
              icon="pi pi-trash"
              size="small"
              severity="danger"
              outlined
              @click.stop="discard(item)"
            />
          </div>
        </li>
      </ul>

      <div class="diff-pane">
        <div class="diff-caption">
          <span class="mono">{{ selected.path }}</span> — baseline at logout (left) vs
          your recovered draft (right)
        </div>
        <DiffViewer :original="selected.baseline" :modified="selected.draft" />
      </div>
    </div>
  </Drawer>
</template>

<style scoped>
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 0.5rem;
}
.title { display: inline-flex; align-items: center; gap: 0.5rem; font-weight: 600; }
.intro {
  margin: 0 0 1rem 0;
  color: var(--retail-hint-text);
  font-size: 0.8125rem;
  line-height: 1.5;
}
.recovery-body {
  display: grid;
  grid-template-columns: 22rem 1fr;
  gap: 1rem;
  /* Fill the drawer below the header + intro so the diff pane has real height. */
  height: calc(100vh - 12rem);
  min-height: 22rem;
}
.file-list {
  list-style: none;
  margin: 0;
  padding: 0;
  background: var(--p-content-background, #fff);
  border: 1px solid var(--p-content-border-color, #e5e7eb);
  border-radius: 4px;
  overflow-y: auto;
}
.file-row {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid var(--p-content-border-color, #e5e7eb);
}
.file-row:last-child { border-bottom: none; }
.file-row.active {
  background: var(--p-highlight-background);
  color: var(--p-highlight-color);
}
.file-meta { display: flex; flex-direction: column; gap: 0.15rem; min-width: 0; }
.file-path { font-size: 0.8125rem; word-break: break-all; }
.file-meta small { color: var(--retail-hint-text); font-size: 0.7rem; }
.file-row.active .file-meta small { color: inherit; }
.file-actions { display: flex; flex-wrap: wrap; gap: 0.4rem; }
.diff-pane { display: flex; flex-direction: column; gap: 0.5rem; min-width: 0; }
.diff-caption { font-size: 0.8125rem; color: var(--retail-hint-text); }
/* DiffViewer is height:100% of its host; let it consume the remaining column height. */
.diff-pane :deep(.diff-viewer) { flex: 1; min-height: 0; }
.mono { font-family: 'Cascadia Code', Consolas, monospace; }
</style>
